<script lang="ts">
  import type { FaceMorphologyReport, LookMorphologyTuningReport } from '../quality/faceMorphology';
  import type { VtoPerformanceTiming } from '../quality/vtoPerformance';
  import type { SemanticFaceMap } from './semanticFaceMap';
  import {
    BEAUTY_SEGMENTATION_LABELS,
    BEAUTY_SEGMENTATION_SCHEMA_VERSION
  } from './beautySegmentationSchema';

  type Props = {
    map: SemanticFaceMap;
    morphology?: FaceMorphologyReport | null;
    lookMorphology?: LookMorphologyTuningReport | null;
    timing?: VtoPerformanceTiming | null;
  };

  let { map, morphology = null, lookMorphology = null, timing = null }: Props = $props();

  const entries = BEAUTY_SEGMENTATION_LABELS.filter((entry) => entry.targetClass);
  const coverageEntries = ['skin', 'leftMobileLid', 'rightMobileLid', 'leftCheek', 'rightCheek', 'lips']
    .map((key) => ({ key, value: map.quality.coverage[key] ?? 0 }));

  function renderMask(canvas: HTMLCanvasElement, mask: HTMLCanvasElement, tint: string) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = 120;
    canvas.height = Math.max(1, Math.round(120 * (map.height / map.width)));
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#111';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const tmp = document.createElement('canvas');
    tmp.width = canvas.width;
    tmp.height = canvas.height;
    const tctx = tmp.getContext('2d');
    if (!tctx) return;
    tctx.fillStyle = tint;
    tctx.fillRect(0, 0, tmp.width, tmp.height);
    tctx.globalCompositeOperation = 'destination-in';
    tctx.drawImage(mask, 0, 0, tmp.width, tmp.height);
    tctx.globalCompositeOperation = 'source-over';

    ctx.drawImage(tmp, 0, 0);
  }

  function maskPreview(canvas: HTMLCanvasElement, params: { mask: HTMLCanvasElement; tint: string }) {
    renderMask(canvas, params.mask, params.tint);
    return {
      update(next: { mask: HTMLCanvasElement; tint: string }) {
        renderMask(canvas, next.mask, next.tint);
      }
    };
  }

  function drawTintedMask(
    ctx: CanvasRenderingContext2D,
    mask: HTMLCanvasElement,
    tint: string,
    x: number,
    y: number,
    width: number,
    height: number
  ) {
    ctx.fillStyle = '#111';
    ctx.fillRect(x, y, width, height);

    const tmp = document.createElement('canvas');
    tmp.width = width;
    tmp.height = height;
    const tctx = tmp.getContext('2d');
    if (!tctx) return;
    tctx.fillStyle = tint;
    tctx.fillRect(0, 0, width, height);
    tctx.globalCompositeOperation = 'destination-in';
    tctx.drawImage(mask, 0, 0, width, height);
    tctx.globalCompositeOperation = 'source-over';
    ctx.drawImage(tmp, x, y);
  }

  function downloadMaskAtlas() {
    const cellW = 360;
    const cellH = Math.round(cellW * (map.height / map.width));
    const labelH = 42;
    const gap = 18;
    const cols = 2;
    const rows = Math.ceil(entries.length / cols);
    const headerH = 96;
    const canvas = document.createElement('canvas');
    canvas.width = cols * cellW + (cols + 1) * gap;
    canvas.height = headerH + rows * (cellH + labelH + gap) + gap;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#fbf4ec';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#201713';
    ctx.font = 'bold 24px sans-serif';
    ctx.fillText('BeautyIQ semantic masks', gap, 36);
    ctx.font = '16px sans-serif';
    ctx.fillText(`${BEAUTY_SEGMENTATION_SCHEMA_VERSION} · ${map.source} · ${Math.round(map.quality.confidence * 100)}%`, gap, 64);
    if (morphology) {
      ctx.fillText(`morphology: eyes ${morphology.eyeAperture}, lid ${morphology.lidSpace}, lips ${morphology.lipFullness}, cheeks ${morphology.cheekSpace}`, gap, 86);
    }
    if (lookMorphology?.rules.length) {
      ctx.fillText(`look rules: ${lookMorphology.rules.length}`, gap + 390, 86);
    }
    if (map.quality.fallbacks.length) {
      ctx.fillText(`fallbacks: ${map.quality.fallbacks.join(', ')}`, gap + 540, 64);
    }

    entries.forEach((entry, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = gap + col * (cellW + gap);
      const y = headerH + row * (cellH + labelH + gap);
      const cell = map.masks[entry.key];
      if (cell) drawTintedMask(ctx, cell, entry.tint, x, y, cellW, cellH);
      ctx.fillStyle = '#fff';
      ctx.fillRect(x, y + cellH, cellW, labelH);
      ctx.fillStyle = '#201713';
      ctx.font = 'bold 15px sans-serif';
      ctx.fillText(`${entry.id}. ${entry.label}`, x + 12, y + cellH + 25);
    });

    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `beautyiq-semantic-masks-${map.source}.png`;
      link.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  }
</script>

<section class="card space-y-3" data-testid="semantic-debug">
  <div>
    <h3 class="font-display text-lg">Debug maschere</h3>
    <p class="text-[11px] text-brand-muted">
      Sorgente: <span class="font-medium text-brand-ink">{map.source}</span>
      · confidence {Math.round(map.quality.confidence * 100)}%
      · parser {map.quality.parserAvailable ? 'on' : 'fallback'}
      {#if map.quality.fallbacks.length}
        · fallback {map.quality.fallbacks.join(', ')}
      {/if}
    </p>
  </div>

  <button
    type="button"
    class="rounded-pill border border-brand-accent px-3 py-2 text-xs font-semibold text-brand-accent"
    data-testid="export-mask-atlas"
    onclick={downloadMaskAtlas}
  >
    Esporta atlas PNG
  </button>

  {#if morphology}
    <div class="rounded-md bg-brand-bg p-3 text-[11px]" data-testid="morphology-debug">
      <h4 class="font-semibold text-brand-ink">Morfologia</h4>
      <dl class="mt-2 grid grid-cols-2 gap-2 text-brand-muted">
        <div>
          <dt class="font-medium text-brand-accent">Occhi</dt>
          <dd>{morphology.eyeAperture}</dd>
        </div>
        <div>
          <dt class="font-medium text-brand-accent">Palpebra</dt>
          <dd>{morphology.lidSpace}</dd>
        </div>
        <div>
          <dt class="font-medium text-brand-accent">Labbra</dt>
          <dd>{morphology.lipFullness}</dd>
        </div>
        <div>
          <dt class="font-medium text-brand-accent">Guance</dt>
          <dd>{morphology.cheekSpace}</dd>
        </div>
      </dl>
      <div class="mt-3" data-testid="look-morphology-debug">
        <h4 class="font-semibold text-brand-ink">Adattamento look</h4>
        {#if lookMorphology?.rules.length}
          <ul class="mt-1 list-disc space-y-1 pl-4 text-brand-muted">
            {#each lookMorphology.rules as rule}
              <li>{rule}</li>
            {/each}
          </ul>
        {:else}
          <p class="mt-1 text-brand-muted">Nessuna regola specifica attiva</p>
        {/if}
      </div>
    </div>
  {/if}

  {#if timing}
    <div class="rounded-md bg-brand-bg p-3 text-[11px]" data-testid="vto-performance-debug">
      <h4 class="font-semibold text-brand-ink">Performance</h4>
      <dl class="mt-2 grid grid-cols-2 gap-2 text-brand-muted">
        <div>
          <dt class="font-medium text-brand-accent">Total</dt>
          <dd>{timing.totalMs} ms</dd>
        </div>
        <div>
          <dt class="font-medium text-brand-accent">Detection</dt>
          <dd>{timing.detectionMs} ms</dd>
        </div>
        <div>
          <dt class="font-medium text-brand-accent">Semantic</dt>
          <dd>{timing.semanticMs} ms</dd>
        </div>
        <div>
          <dt class="font-medium text-brand-accent">Render</dt>
          <dd>{timing.renderMs} ms</dd>
        </div>
        <div>
          <dt class="font-medium text-brand-accent">Skin</dt>
          <dd>{timing.skinMs} ms</dd>
        </div>
        <div>
          <dt class="font-medium text-brand-accent">Base</dt>
          <dd>{timing.complexionMs} ms</dd>
        </div>
        <div>
          <dt class="font-medium text-brand-accent">Eyes</dt>
          <dd>{timing.eyeMs} ms</dd>
        </div>
        <div>
          <dt class="font-medium text-brand-accent">Lips</dt>
          <dd>{timing.lipMs} ms</dd>
        </div>
        <div>
          <dt class="font-medium text-brand-accent">Refinement</dt>
          <dd>{timing.refinementMs} ms</dd>
        </div>
      </dl>
    </div>
  {/if}

  <div class="rounded-md bg-white p-3 text-[11px]" data-testid="semantic-coverage-debug">
    <h4 class="font-semibold text-brand-ink">Copertura maschere</h4>
    <dl class="mt-2 grid grid-cols-2 gap-2 text-brand-muted">
      {#each coverageEntries as entry}
        <div>
          <dt class="font-medium text-brand-accent">{entry.key}</dt>
          <dd>{(entry.value * 100).toFixed(2)}%</dd>
        </div>
      {/each}
    </dl>
  </div>

  <div class="grid grid-cols-2 gap-2">
    {#each entries as entry (entry.key)}
      {@const mask = map.masks[entry.key as keyof typeof map.masks]}
      {#if mask}
      <div class="overflow-hidden rounded-card border border-black/10 bg-white">
        <canvas
          class="block w-full bg-black"
          use:maskPreview={{ mask, tint: entry.tint }}
        ></canvas>
        <div class="px-2 py-1 text-[10px] font-medium text-brand-ink">{entry.label}</div>
      </div>
      {/if}
    {/each}
  </div>
</section>
